/**
 * useLocale — minimal locale hook for Celestia (board-game-client).
 *
 * Cloned from playground; UI is NOT shared (CLAUDE.md).
 * Returns a `t(key, params?)` function resolving against the active locale.
 * Falls back to the key itself when missing.
 *
 * `t` supports a tiny ICU MessageFormat subset sufficient for this UI:
 *   - simple argument:  "Phase: {phase}"
 *   - cardinal plural:  "{n, plural, one {# card} other {# cards}}"
 *     with CLDR plural-category selection (en: one/other, ko: other).
 * No external dependency — the full intl-messageformat is overkill here.
 */

import { DEFAULT_LOCALE, type LocaleCode, locales } from "./index";

export type MessageParams = Readonly<Record<string, string | number>>;

export interface UseLocaleReturn {
  readonly t: (key: string, params?: MessageParams) => string;
  readonly locale: LocaleCode;
}

/**
 * CLDR cardinal plural category for the locales this UI ships.
 * Korean (`ko`) has a single category → always `other`.
 * English (`en`) uses the standard `one` (n === 1) / `other`.
 */
function pluralCategory(locale: LocaleCode, n: number): "one" | "other" {
  if (locale === "ko-KR") return "other";
  return n === 1 ? "one" : "other";
}

// Hard invariant: at most ONE `{x, plural, …}` block per message, and it must
// be the trailing token. The `$`-anchored lazy match only resolves the last
// plural block — multi-plural templates are unsupported by design (asserted in
// i18n.test.ts). Add a brace-balancing scanner here before ever introducing one.
const PLURAL_RE = /\{(\w+),\s*plural,\s*([\s\S]*?)\}\s*$/;

function selectPluralBody(options: string, category: "one" | "other"): string {
  // options := "one {# card} other {# cards}" — extract the matching arm,
  // fall back to `other` (CLDR requires an `other` arm).
  const arms: Record<string, string> = {};
  const re = /(\w+)\s*\{([^{}]*)\}/g;
  let m: RegExpExecArray | null = re.exec(options);
  while (m !== null) {
    if (m[1] !== undefined && m[2] !== undefined) arms[m[1]] = m[2];
    m = re.exec(options);
  }
  return arms[category] ?? arms.other ?? "";
}

function format(
  template: string,
  params: MessageParams,
  locale: LocaleCode,
): string {
  const plural = PLURAL_RE.exec(template);
  if (plural?.[1] !== undefined && plural[2] !== undefined) {
    const arg = plural[1];
    const raw = params[arg];
    const n = typeof raw === "number" ? raw : Number(raw ?? 0);
    const body = selectPluralBody(plural[2], pluralCategory(locale, n));
    const prefix = template.slice(0, plural.index);
    return interpolate(prefix + body.replace(/#/g, String(n)), params);
  }
  return interpolate(template, params);
}

function interpolate(template: string, params: MessageParams): string {
  return template.replace(/\{(\w+)\}/g, (_full, name: string) => {
    const v = params[name];
    return v === undefined ? `{${name}}` : String(v);
  });
}

export function useLocale(
  locale: LocaleCode = DEFAULT_LOCALE,
): UseLocaleReturn {
  const map = locales[locale];

  const t = (key: string, params?: MessageParams): string => {
    const template = map[key];
    if (template === undefined) return key;
    if (params === undefined) return template;
    return format(template, params, locale);
  };

  return { t, locale };
}
